import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { toast } from "react-toastify";
import { yupResolver } from "@hookform/resolvers/yup";
import { FieldError, useForm } from "react-hook-form";
import { ApolloQueryResult } from "@apollo/client";

import { PanelWidth } from "../../../../components/panel/Panel";
import {
  DagCommunityViewQuery,
  Exact,
  useConvertCommunityDagsToScrMutation,
  useConvertDagsToD2TMutation,
  ViewerQuery,
} from "../../../../graphql/schema";
import { Button } from "../../../../components/button/Button";
import { Color } from "../../../../services/buttonLinkConstants";
import { Layout } from "../../../../components/layout/Layout";
import { DataFraction } from "../../../../components/data-fraction/DataFraction";
import DateTime from "../../../../components/date-time/DateTime";
import { Modal, ModalKind } from "../../../../components/modal/Modal";
import { showGraphqlValidationErrors } from "../../../../services/showGraphqlValidationErrors";
import Field from "../../../../components/field/Field";
import { Link } from "../../../../components/link/Link";
import { CopyToClipboard } from "../../../../components/copy-to-clipboard/CopyToClipBoard";
import { IconBind } from "../../../../components/icon/IconBind";

import styles from "./dag-community-view.module.scss";

export interface DagCommunityDagsToBrokerProps {
  me: ViewerQuery | undefined;
  totalDagsQuery: DagCommunityViewQuery | undefined;
  currentRate: string;
  meRefetch: (
    variables?:
      | Partial<
          Exact<{
            [key: string]: never;
          }>
        >
      | undefined,
  ) => Promise<ApolloQueryResult<ViewerQuery>>;
  className?: string;
}

interface BurnDagsD2TFormInfo {
  totalAmount: string;
  name: string;
  email: string;
  convertBalance?: boolean;
  // username: string;
  fconfirm: boolean;
  accountOpened: boolean;
  acknowledgement: boolean;
  acknowledgementTrading: boolean;
}

interface CommunityBurnDagsD2TFormInfo {
  totalAmount: string;
  name: string;
  email: string;
  convertBalance?: boolean;
  fconfirm: boolean;
  accountOpened: boolean;
  acknowledgement: boolean;
  acknowledgementTrading: boolean;
}

const burnDagsD2TValidationSchema: yup.SchemaOf<BurnDagsD2TFormInfo> = yup.object().shape({
  totalAmount: yup.string().required("Amount is required"),
  name: yup.string().required("Name is required"),
  email: yup.string().email().required("Email is required"),
  convertBalance: yup.boolean().optional(),
  // username: yup.string().required("Username is required"),
  fconfirm: yup
    .boolean()
    .oneOf([true], "Please check that you understand the terms")
    .required("Please check that you understand the terms"),
  accountOpened: yup
    .boolean()
    .oneOf([true], "Please check that you have opened TAG Markets account")
    .required("Please check that you have opened TAG Markets account"),
  acknowledgement: yup
    .boolean()
    .oneOf([true], "Please check that you understand that the burn and send is final and can not be reversed.")
    .required("Please check that you understand that the burn and send is final and can not be reversed."),
  acknowledgementTrading: yup
    .boolean()
    .oneOf(
      [true],
      "Please check that you understand that D2T balance can only be used for trading and is non-withdrawable.",
    )
    .required(
      "Please check that you understand that D2T balance can only be used for trading and is non-withdrawable.",
    ),
});

const membershipBurnDagsD2TValidationSchema: yup.SchemaOf<CommunityBurnDagsD2TFormInfo> = yup.object().shape({
  totalAmount: yup.string().required("Amount is required"),
  name: yup.string().required("Name is required"),
  email: yup.string().email().required("Email is required"),
  convertBalance: yup.boolean().optional(),
  fconfirm: yup
    .boolean()
    .oneOf([true], "Please check that you understand the terms")
    .required("Please check that you understand the terms"),
  accountOpened: yup
    .boolean()
    .oneOf([true], "Please check that you have opened TAG Markets account")
    .required("Please check that you have opened TAG Markets account"),
  acknowledgement: yup
    .boolean()
    .oneOf([true], "Please check that you understand that the burn and send is final and can not be reversed.")
    .required("Please check that you understand that the burn and send is final and can not be reversed."),
  acknowledgementTrading: yup
    .boolean()
    .oneOf(
      [true],
      "Please check that you understand that D2T balance can only be used for trading and is non-withdrawable.",
    )
    .required(
      "Please check that you understand that D2T balance can only be used for trading and is non-withdrawable.",
    ),
});

function useBurnDagsD2TForm() {
  const { register, handleSubmit, errors, reset, setError } = useForm<BurnDagsD2TFormInfo>({
    mode: "all",
    reValidateMode: "onChange",
    resolver: yupResolver(burnDagsD2TValidationSchema),
  });

  return {
    register,
    handleSubmit,
    errors,
    reset,
    setError,
  };
}

function useMembershipBurnDagsD2TForm() {
  const { register, handleSubmit, errors, reset, setError } = useForm<BurnDagsD2TFormInfo>({
    mode: "all",
    reValidateMode: "onChange",
    resolver: yupResolver(membershipBurnDagsD2TValidationSchema),
  });

  return {
    register,
    handleSubmit,
    errors,
    reset,
    setError,
  };
}

export const DagCommunityDagsToBroker: FC<DagCommunityDagsToBrokerProps> = ({
  me,
  meRefetch,
  currentRate,
  totalDagsQuery,
}) => {
  const { t } = useTranslation();
  const [isCommunityBurnMode, setIsCommunityBurnMode] = useState(false);
  const [isReadMoreOpen, setIsReadMoreOpen] = useState(false);
  const [isBurnD2TMode, setIsBurnD2TMode] = useState(false);
  const [communityAmount, setCommunityAmount] = useState("");
  const [amountD2T, setAmountD2T] = useState("");

  const [convertCommunityDags] = useConvertCommunityDagsToScrMutation();
  const [convertDagsD2T] = useConvertDagsToD2TMutation();

  const {
    register: burnDagsD2T,
    handleSubmit: handleBurnDagsD2TSubmit,
    errors: burnDagsD2TInputErrors,
    reset: resetBurnD2TDags,
    setError: setBurnDagsD2TError,
  } = useMembershipBurnDagsD2TForm();
  const {
    register: communityBurnDags,
    handleSubmit: handleCommunityBurnDagsSubmit,
    errors: communityBurnDagsInputErrors,
    reset: resetCommunityBurnDags,
    setError: setCommunityBurnDagsError,
  } = useBurnDagsD2TForm();

  function getDagsD2TAmount() {
    return parseFloat(me?.me.meD2TInfo?.totalDags ?? "0") - parseFloat(me?.me.meD2TInfo?.usedDags ?? "0");
  }

  function getScrD2TAmount() {
    return (getDagsD2TAmount() / parseFloat(me?.me.meD2TInfo?.ratio ?? "0")).toFixed(2);
  }

  function floorToThreeDecimals(num: number) {
    return Math.floor(num * 1000) / 1000;
  }

  const totalDagsD2T = getDagsD2TAmount();
  const totalCommunityDags = me?.viewer?.coinAccountBalance ?? 0;

  const handlePercentageD2T = (percent: string) => {
    setAmountD2T(((totalDagsD2T * parseFloat(percent)) / 100).toString());
  };
  const handleCommunityPercentageD2T = (percent: string) => {
    setCommunityAmount(((totalCommunityDags * parseFloat(percent)) / 100).toString());
  };

  const handleCommunityBurnDagsFormSubmit = handleCommunityBurnDagsSubmit(async ({ totalAmount, name, email }) => {
    try {
      const result = await convertCommunityDags({
        variables: { totalAmount, name, email, convertBalance: false },
      });
      if (result.data) {
        toast.success(t("DAGS burned to D2T successfully!"));
        resetCommunityBurnDags();
        setCommunityAmount("");
        meRefetch();
        // setIsWebinarsModalOpen(false);
      } else {
        const errorMessage = result?.errors?.[0]?.message;
        if (errorMessage) {
          const myCustomFieldError: FieldError = {
            type: "required",
            message: errorMessage,
            ref: undefined,
          };
          communityBurnDagsInputErrors.totalAmount = myCustomFieldError;

          setCommunityBurnDagsError("totalAmount", {
            type: "manual",
            message: errorMessage || t("An unexpected error occurred"),
          });
        } else {
          toast.error(t("Something went wrong"));
        }
      }
    } catch (err: any) {
      if (err?.message?.includes("Marketing Fee required to burn for others")) {
        setCommunityBurnDagsError("email", {
          type: "manual",
          message: err?.message || t("An unexpected error occurred"),
        });
      } else {
        setCommunityBurnDagsError("totalAmount", {
          type: "manual",
          message: err?.message || t("An unexpected error occurred"),
        });
      }
      if (!showGraphqlValidationErrors(t, err) && err instanceof Error) {
        toast.error(err?.message);
      }
    }
  });

  const handleBurnDagsFormD2TSubmit = handleBurnDagsD2TSubmit(async ({ totalAmount, name, email, convertBalance }) => {
    const test = (parseFloat(totalAmount) / parseFloat(me?.me.meD2TInfo?.ratio ?? "0")).toFixed(2);
    if (parseFloat(test) < 25) {
      //   const myCustomFieldError: FieldError = {
      //     type: "required",
      //     message: t("Submitted D2T amount must be at least 25"),
      //     ref: undefined,
      //   };
      //   burnDagsD2TInputErrors.totalAmount = myCustomFieldError;
      setBurnDagsD2TError("totalAmount", {
        type: "manual",
        message: t("Submitted D2T amount must be at least 25"),
      });
      return;
    }
    try {
      const result = await convertDagsD2T({
        variables: { totalAmount, name, email, convertBalance: convertBalance ?? false },
      });
      if (result.data) {
        toast.success(t("DAGS burned to D2T successfully!"));
        resetBurnD2TDags();
        setAmountD2T("");
        meRefetch();
        // setIsWebinarsModalOpen(false);
      } else {
        const errorMessage = result?.errors?.[0]?.message;
        if (errorMessage) {
          const myCustomFieldError: FieldError = {
            type: "required",
            message: errorMessage,
            ref: undefined,
          };
          burnDagsD2TInputErrors.totalAmount = myCustomFieldError;

          setBurnDagsD2TError("totalAmount", {
            type: "manual",
            message: errorMessage || t("An unexpected error occurred"),
          });
        } else {
          toast.error(t("Something went wrong"));
        }
      }
    } catch (err: any) {
      setBurnDagsD2TError("totalAmount", {
        type: "manual",
        message: err?.message || t("An unexpected error occurred"),
      });
      if (!showGraphqlValidationErrors(t, err) && err instanceof Error) {
        toast.error(err?.message);
      }
    }
  });

  return (
    <>
      <h2 style={{ margin: "15px 0" }}>DAGS x TAG Markets Collaboration – Amplify Your Trading Power!</h2>
      <p
        dangerouslySetInnerHTML={{
          __html: t(
            t(
              `We’re excited to introduce a powerful new opportunity with TAG Markets! Their Amplify 12x account lets traders maximize capital — boosting deposits 12X with no restrictions except a 10% drawdown limit — a perfect alternative to prop firms without the extra costs.<br/><br/><strong>Here's the Deal</strong>:<br/>Now, you can <strong>cover 50% of your deposit with DAGS</strong>! Simply burn bonus DAGS from your membership, and you’ll receive up to your full membership value in <strong>D2T (DAGS to TAG) promotional balance</strong> to use as part of your deposit — <strong>50% from DAGS, 50% in fiat/crypto</strong>.<br/><br/><p>1 D2T gives 1 USD deposit in TAG Markets.</p><br/><br/>Get started today and take your trading to the next level! <strong><a href="{{url}}" rel="noopener noreferrer" target="_blank">Open your account</a></strong>`,
            ),
            { url: "https://aitech.ibportal.io/auth/register?e=jWUZXlM4mNCmBiI4M_6C-7xP4xKECwtmvRJtKO2JoGw&a=1" },
          ),
        }}
      />
      <p style={{ margin: "20px 0" }}>
        <Button
          onClick={() => setIsReadMoreOpen(true)}
          className={styles["access-product-link"]}
          borderRadius="MEDIUM"
          color={Color.DARK_BLUE}
          fontSize={16}
          width={300}
        >
          <span className={styles["link-label"]}>{t("How it works - see the steps!")}</span>
        </Button>
      </p>
      <ul>
        <li className={styles["dag-wrap"]}>
          <div className={styles["dag-section"]}>
            <div>
              <h3 className={styles.name}>Burn DAGS and receive D2T</h3>
              <span style={{ color: "#d87333", fontSize: "12px" }}>Membership Burn</span>
              <br />
              <br />
              <span style={{ color: "#d87333", fontSize: "12px" }}>
                *Special burn rate limited to your membership quantity
              </span>
            </div>
          </div>

          <Layout className={styles["dag-data"]}>
            <DataFraction
              className={styles["data-fraction"]}
              icon={undefined}
              label={t("Your DAGS balance")}
              value={`${me?.viewer?.coinAccountBalance} ${t("DAGS")}`}
            />
            <DataFraction
              className={styles["data-fraction"]}
              icon={undefined}
              label={t("DAGS You can burn")}
              value={`${getDagsD2TAmount()} ${t("DAGS")}`}
            />

            <DataFraction
              className={styles["data-fraction"]}
              icon={undefined}
              label={t("You receive")}
              value={`${getScrD2TAmount()} ${t("D2T")}`}
            />

            <Button
              className={styles["access-product-link"]}
              borderRadius="MEDIUM"
              color={Color.DARK_BLUE}
              fontSize={14}
              onClick={() => {
                setIsBurnD2TMode(true);
              }}
            >
              <span className={styles["link-label"]}>{t("Go to Burn")}</span>
            </Button>
          </Layout>
        </li>
        <li className={styles["dag-wrap"]}>
          <div className={styles["dag-section"]}>
            <div>
              <h3 className={styles.name}>Burn DAGS and receive D2T</h3>
              <span style={{ color: "#d87333", fontSize: "12px" }}>Community Burn</span>
              <br />
              <br />
              <span style={{ color: "#d87333", fontSize: "12px" }}>
                *Preferred burn rate for unlimited quantity. Deposit and burn as many as you want
              </span>
            </div>
          </div>

          <Layout className={styles["dag-data"]}>
            <DataFraction
              className={styles["data-fraction"]}
              icon={undefined}
              label={t("Your DAGS balance")}
              value={`${me?.viewer?.coinAccountBalance} ${t("DAGS")}`}
            />
            <DataFraction
              className={styles["data-fraction"]}
              icon={undefined}
              label={t("DAGS You can burn")}
              value={`${t("... DAGS")}`}
            />
            {totalCommunityDags && totalCommunityDags > 0 ? (
              <DataFraction
                className={styles["data-fraction"]}
                icon={undefined}
                label={t("You receive")}
                value={`${t("... D2T")}`}
              />
            ) : (
              <DataFraction
                className={styles["data-fraction"]}
                icon={undefined}
                label={t("You receive")}
                value={`${t("... D2T")}`}
              />
            )}

            <Button
              className={styles["access-product-link"]}
              borderRadius="MEDIUM"
              color={Color.DARK_BLUE}
              fontSize={14}
              onClick={() => {
                setIsCommunityBurnMode(true);
              }}
            >
              <span className={styles["link-label"]}>{t("Go to Burn")}</span>
            </Button>
          </Layout>
        </li>
      </ul>
      {me?.me?.meD2TInfo?.burned &&
        me?.me?.meD2TInfo?.burned?.map((burned, index) => (
          <div key={`${index}-term`} className={styles["term-grid"]}>
            <div>
              {burned.name}
              {burned.email && ` (${burned.email})`}
            </div>
            <div>
              <strong>D2T: </strong>
              {parseFloat(burned.scr).toFixed(2)}
            </div>
            <div>
              {burned.toBalance > 0 ? (
                "Converted as D2T Balance"
              ) : (
                <CopyToClipboard
                  iconColor
                  copyText={burned.txAddress ?? ""}
                  onClick={() => {
                    toast.success(t("TX hash copied"));
                  }}
                  icon={<IconBind fill="#ff871c" width={18} height={18} />}
                >
                  {burned.txAddress}
                </CopyToClipboard>
              )}
            </div>
            <div>
              <DateTime value={burned.createdAt} />
            </div>
            <div>
              <strong>Burned DAGS:</strong> {burned.dags}
            </div>
          </div>
        ))}
      <Modal
        isOpen={isBurnD2TMode}
        close={() => {
          setIsBurnD2TMode(false);
        }}
        kind={ModalKind.PRIMARY}
        panelProps={{
          imagePath: "/images/crowd-icon.svg",
          width: PanelWidth.SMALL,
          title: t("DAGS Community"),
          caption: t("Burn DAGS and receive D2T"),
          label: t("DAGS Community"),
          titleClassName: styles["modal-title"],
          captionClassName: styles["modal-caption"],
        }}
      >
        <div>
          {/* Burn DAGS Content */}
          <h2 style={{ textAlign: "center", marginTop: "30px" }}>
            {t("Burn DAGS and send D2T balance to TAG Markets")}
          </h2>
          <form onSubmit={handleBurnDagsFormD2TSubmit}>
            <div style={{ margin: "20px 0" }}>
              <Field
                type="text"
                className={styles.field}
                label={t("Full name registered in TAG Markets")}
                name="name"
                gutter={20}
                error={burnDagsD2TInputErrors.name}
                internalRef={burnDagsD2T}
                value={""}
                onChange={(e) => {
                  // setAmount(e);
                }}
              />
            </div>
            <div style={{ margin: "20px 0" }}>
              <Field
                type="text"
                className={styles.field}
                label={t("E-mail registered in TAG Markets")}
                name="email"
                gutter={20}
                error={burnDagsD2TInputErrors.email}
                internalRef={burnDagsD2T}
                value={""}
                onChange={(e) => {
                  // setAmount(e);
                }}
              />
            </div>
            <div style={{ margin: "20px 0" }}>
              <Field
                type="number"
                className={styles.field}
                label={t("Total DAGS to burn")}
                name="totalAmount"
                gutter={20}
                error={burnDagsD2TInputErrors.totalAmount}
                internalRef={burnDagsD2T}
                value={amountD2T}
                onChange={(e) => {
                  setAmountD2T(e);
                }}
              />
            </div>
            {/* <div style={{ margin: "20px 0" }}>
              <Field
                type="hidden"
                className={styles.field}
                label={t("Convert to D2T balance")}
                name="convertBalance"
                gutter={20}
                checked={true}
                error={burnDagsD2TInputErrors.convertBalance}
                internalRef={burnDagsD2T}
              />
            </div> */}
            <p>
              <strong>You receive D2T (USD):</strong>{" "}
              <strong>
                {parseFloat(amountD2T ?? "0") > 0
                  ? (parseFloat(amountD2T ?? "0") / parseFloat(me?.me?.meD2TInfo?.ratio ?? "0")).toFixed(2)
                  : "0"}
              </strong>
            </p>
            <p>
              <strong>Your special burn rate:</strong>{" "}
              <span style={{ fontWeight: "bold" }}>
                {floorToThreeDecimals(1 / parseFloat(me?.me?.meD2TInfo?.ratio ?? "0") / 1).toFixed(3)}
              </span>
            </p>
            <div style={{ display: "flex", justifyContent: "space-between", margin: "15px 0" }}>
              {["0", "25", "50", "75", "100"].map((percent) => (
                <button
                  type={"button"}
                  key={percent}
                  onClick={() => handlePercentageD2T(percent)}
                  style={{
                    flex: 1,
                    margin: "0 5px",
                    padding: "10px",
                    backgroundColor: "#007bff",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  {percent}%
                </button>
              ))}
            </div>
            <div style={{ margin: "20px 0" }}>
              <input
                type="range"
                min="0"
                max={totalDagsD2T}
                value={amountD2T}
                onChange={(e) => {
                  if (parseFloat(e.target.value) > totalDagsD2T) {
                    setAmountD2T(totalDagsD2T.toString());
                  } else {
                    setAmountD2T(parseFloat(e.target.value).toString());
                  }
                }}
                style={{ width: "100%" }}
              />
            </div>
            <Field
              type="checkbox"
              className={styles.field}
              label={t("I have already opened TAG Markets account.")}
              name="accountOpened"
              gutter={20}
              error={burnDagsD2TInputErrors.accountOpened}
              internalRef={burnDagsD2T}
            />
            <Field
              type="checkbox"
              className={styles.field}
              label={t("I acknowledge that the burn and send is final and can not be reversed.")}
              name="acknowledgement"
              gutter={20}
              error={burnDagsD2TInputErrors.acknowledgement}
              internalRef={burnDagsD2T}
            />
            <Field
              type="checkbox"
              className={styles.field}
              label={t("I acknowledge that the D2T balance can only be used for trading and is non-withdrawable.")}
              name="acknowledgementTrading"
              gutter={20}
              error={burnDagsD2TInputErrors.acknowledgementTrading}
              internalRef={burnDagsD2T}
            />
            <Field
              type="checkbox"
              className={styles.field}
              label={t(
                "I confirm that I have reviewed all details, and I understand the terms of the DAGS burn and D2T balance conversion.",
              )}
              name="fconfirm"
              gutter={20}
              error={burnDagsD2TInputErrors.fconfirm}
              internalRef={burnDagsD2T}
            />
            <div style={{ textAlign: "center", width: "100%", alignItems: "center" }}>
              <Button
                className={styles["access-product-link"]}
                borderRadius="MEDIUM"
                color={Color.DARK_BLUE}
                fontSize={14}
                width={200}
              >
                <span className={styles["link-label"]}>{t("Burn DAGS and Send D2T Balance")}</span>
              </Button>
            </div>
          </form>
        </div>
      </Modal>
      <Modal
        isOpen={isCommunityBurnMode}
        close={() => {
          setIsCommunityBurnMode(false);
        }}
        kind={ModalKind.PRIMARY}
        panelProps={{
          imagePath: "/images/crowd-icon.svg",
          width: PanelWidth.SMALL,
          title: t("DAGS Community"),
          caption: t("Burn DAGS and receive D2T"),
          label: t("DAGS Community"),
          titleClassName: styles["modal-title"],
          captionClassName: styles["modal-caption"],
        }}
      >
        <div>
          {/* Burn DAGS Content */}
          <h2 style={{ textAlign: "center", marginTop: "30px" }}>
            {t("Burn DAGS and send D2T balance to TAG Markets")}
          </h2>
          <form onSubmit={handleCommunityBurnDagsFormSubmit}>
            <div style={{ margin: "20px 0" }}>
              <Field
                type="text"
                className={styles.field}
                label={t("Full name registered in TAG Markets")}
                name="name"
                gutter={20}
                error={communityBurnDagsInputErrors.name}
                internalRef={communityBurnDags}
                value={""}
                onChange={(e) => {
                  // setAmount(e);
                }}
              />
            </div>
            <div style={{ margin: "20px 0" }}>
              <Field
                type="text"
                className={styles.field}
                label={t("E-mail registered in TAG Markets")}
                name="email"
                gutter={20}
                error={communityBurnDagsInputErrors.email}
                internalRef={communityBurnDags}
                value={""}
                onChange={(e) => {
                  // setAmount(e);
                }}
              />
            </div>
            {/* <div style={{ margin: "20px 0" }}>
                <Field
                  type="text"
                  className={styles.field}
                  label={t("User's SF Suite username")}
                  name="username"
                  gutter={20}
                  error={communityBurnDagsInputErrors.username}
                  internalRef={communityBurnDags}
                  value={""}
                  onChange={(e) => {
                    // setAmount(e);
                  }}
                />
              </div> */}
            <div style={{ margin: "20px 0" }}>
              <Field
                type="number"
                className={styles.field}
                label={t("Total DAGS to burn")}
                name="totalAmount"
                gutter={20}
                error={communityBurnDagsInputErrors.totalAmount}
                internalRef={communityBurnDags}
                value={communityAmount}
                onChange={(e) => {
                  if (parseFloat(e) > totalCommunityDags) {
                    setCommunityAmount(totalCommunityDags.toString());
                  } else {
                    setCommunityAmount(e);
                  }
                }}
              />
            </div>
            {/* <div style={{ margin: "20px 0" }}>
              <Field
                type="checkbox"
                className={styles.field}
                label={t("Convert to D2T balance")}
                name="convertBalance"
                gutter={20}
                checked={true}
                error={communityBurnDagsInputErrors.convertBalance}
                internalRef={communityBurnDags}
              />
            </div> */}
            <p>
              <strong>You receive D2T (USD):</strong>{" "}
              <strong>
                {parseFloat(communityAmount ?? "0") > 0
                  ? (parseFloat(communityAmount ?? "0") / (totalDagsQuery?.getTotalBurned?.rate ?? 500)).toFixed(2)
                  : "0"}
              </strong>
            </p>
            <p>
              <strong>Your burn rate:</strong> <span style={{ fontWeight: "bold" }}>{currentRate}</span>
            </p>
            <div style={{ display: "flex", justifyContent: "space-between", margin: "15px 0" }}>
              {["0", "25", "50", "75", "100"].map((percent) => (
                <button
                  type={"button"}
                  key={percent}
                  onClick={() => handleCommunityPercentageD2T(percent)}
                  style={{
                    flex: 1,
                    margin: "0 5px",
                    padding: "10px",
                    backgroundColor: "#007bff",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  {percent}%
                </button>
              ))}
            </div>
            <div style={{ margin: "20px 0" }}>
              <input
                type="range"
                min="0"
                max={totalCommunityDags}
                value={communityAmount}
                onChange={(e) => {
                  if (parseFloat(e.target.value) > totalCommunityDags) {
                    setCommunityAmount(totalCommunityDags.toString());
                  } else {
                    setCommunityAmount(parseFloat(e.target.value).toString());
                  }
                }}
                style={{ width: "100%" }}
              />
            </div>
            <Field
              type="checkbox"
              className={styles.field}
              label={t("I have already opened TAG Markets account.")}
              name="accountOpened"
              gutter={20}
              error={communityBurnDagsInputErrors.accountOpened}
              internalRef={communityBurnDags}
            />
            <Field
              type="checkbox"
              className={styles.field}
              label={t("I acknowledge that the burn and send is final and can not be reversed.")}
              name="acknowledgement"
              gutter={20}
              error={communityBurnDagsInputErrors.acknowledgement}
              internalRef={communityBurnDags}
            />
            <Field
              type="checkbox"
              className={styles.field}
              label={t("I acknowledge that the D2T balance can only be used for trading and is non-withdrawable.")}
              name="acknowledgementTrading"
              gutter={20}
              error={communityBurnDagsInputErrors.acknowledgementTrading}
              internalRef={communityBurnDags}
            />
            <Field
              type="checkbox"
              className={styles.field}
              label={t(
                "I confirm that I have reviewed all details, and I understand the terms of the DAGS burn and D2T balance conversion.",
              )}
              name="fconfirm"
              gutter={20}
              error={communityBurnDagsInputErrors.fconfirm}
              internalRef={communityBurnDags}
            />
            <div style={{ textAlign: "center", width: "100%", alignItems: "center" }}>
              <Button
                className={styles["access-product-link"]}
                borderRadius="MEDIUM"
                color={Color.DARK_BLUE}
                fontSize={14}
                width={200}
              >
                <span className={styles["link-label"]}>{t("Burn DAGS and Send D2T Balance")}</span>
              </Button>
            </div>
          </form>
        </div>
      </Modal>
      <Modal
        isOpen={isReadMoreOpen}
        close={() => {
          setIsReadMoreOpen(false);
        }}
        kind={ModalKind.PRIMARY}
        panelProps={{
          imagePath: "/images/crowd-icon.svg",
          width: PanelWidth.SMALL,
          title: t("DAGS Community"),
          caption: t("Burn DAGS and receive D2T"),
          label: t("DAGS Community"),
          titleClassName: styles["modal-title"],
          captionClassName: styles["modal-caption"],
        }}
      >
        <div style={{ padding: "20px" }}>
          <h2 style={{ textAlign: "center", marginTop: "30px", marginBottom: "30px" }}>{t("What is TAG Markets?")}</h2>
          <p style={{ marginBottom: "20px" }}>
            TAG Markets is a regulated and transparent brokerage offering zero deposit and withdrawal fees, fast
            withdrawals, and a rapidly growing user base.
          </p>
          <h3>{t("How to Open Your Account and Make a Deposit:")}</h3>
          <ol>
            <li>
              {t("Open your Amplify 12X account with TAG Markets using this link:")}{" "}
              <a
                href="https://aitech.ibportal.io/auth/register?e=jWUZXlM4mNCmBiI4M_6C-7xP4xKECwtmvRJtKO2JoGw&a=1"
                target="_blank"
                rel="noreferrer"
              >
                {t("Sign up here")}
              </a>
            </li>
            <li style={{ marginTop: "20px" }}>
              {t(
                "Burn your DAGS and receive D2T promotional balance. Each D2T = $1 in deposit value for your brokerage account.",
              )}
              <p style={{ marginTop: "10px", marginBottom: "10px" }}>
                Before you burn please check in the 12x Funded section in TAG Markets for the different account options
                (which total deposit you want to do) so you know how much USD deposit and how much D2T deposit you need
                for the account you wish to open.
              </p>
            </li>
            <li>
              {t("Go to Burn:")}
              <ul style={{ listStyleType: "none" }}>
                <li>{t("Enter your full name and email used for TAG Markets registration.")}</li>
                <li>{t("Choose the amount of DAGS to burn.")}</li>
                <li>
                  {t(
                    "Send the received D2T balance to TAG Markets. This balance will cover max 50% of your total deposit.",
                  )}
                </li>
              </ul>
            </li>
            <li>
              {t("Complete your deposit:")}
              <br />
              {t("Make the remaining 50% deposit to your TAG Markets account via card, crypto, or wire transfer.")}
            </li>
            <li style={{ marginTop: "20px" }}>
              {t("Request for your 12x account:")}
              <br />
              <span
                dangerouslySetInnerHTML={{
                  __html: t(
                    `Once both deposits are done, go to (<a href="{{link}}" target="_blank" rel="noreferrer">{{link}}</a>) and fill in the form to request for the opening of a 12x account.`,
                    {
                      link: "https://form.jotform.com/tagforms2025/d2ttoken-accounts",
                    },
                  ),
                }}
              />
              <br />
              <br />
              <p>The form has the following steps to fill in:</p>{" "}
              <ol>
                <li>Fill in your full name</li>
                <li>Your e-mail address registered at TAG Markets</li>
                <li>USD deposit made at TAG Markets</li>
                <li>Value of burned tokens in USD (number of D2T)</li>
                <li>
                  Add the screenshot of tokens burned (after burning you see the burn info listed under the burn
                  section)
                </li>
                <li>
                  If you wish to upgrade - “skip” (this you can use if you want to increase the account balance later)
                </li>
                <li>Click “Send”</li>
              </ol>
              <br />
            </li>
            <li>{t("Now you should get a confirmation email of 12x account opening within 24h.")}</li>
            <li>
              {t("Start trading!")}
              <br />
              {t(
                "Once your 12x account is opened and fully funded, you are free to trade on your own, follow some strategies or even copy other experts - exactly how you wish.",
              )}
            </li>
          </ol>
          <div style={{ marginLeft: "40px" }}>
            <p style={{ marginTop: "10px", marginBottom: "10px" }}>
              PS: In case you a looking for an automatic solution, there are some good strategies that have joined TAG
              Market’s social trading area. No extra charge, just subscribe, activate and system copies the trades
              automatically.
            </p>
            {/* <div style={{ marginTop: "10px", marginBottom: "10px", maxWidth: "500px" }}>
              <ol type="A">
                <li style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "5px" }}>
                  <span>Multistrategy (AI + human)</span>
                  <Link
                    color="DARK_BLUE"
                    borderRadius="SMALL"
                    weight="BOLD"
                    to={""}
                    externalUrl={"https://social.tagmarkets.com/portal/registration/subscription/84903/iTraderAl"}
                  >
                    {t("Start Copying")}
                  </Link>
                </li>
              </ol>
            </div> */}
            <p style={{ marginTop: "10px", marginBottom: "10px" }}>
              <a
                href="https://intercom.help/tag-markets/en/articles/10397021-how-to-link-your-account-to-a-private-copy"
                rel="noopener noreferrer"
                target="_blank"
              >
                An explanation how to link to a copy
              </a>
            </p>
            <p style={{ marginTop: "10px", marginBottom: "25px" }}>
              Trading account ID and password can be found in the email you received about your 12x account opening, or
              in the Account Summary section
            </p>
          </div>
          <h3>{t("Key rules for the Amplify 12X account:")}</h3>
          <ul style={{ marginLeft: "40px" }}>
            <li>{t("Minimum deposit is 50 USD - can be 25 from D2T balance and 25 from USD")}</li>
            <li>{t("Balance from D2T can be used for trading only")}</li>
            <li>{t("Initial deposit (via card/crypto/wire) can be withdrawn after 30 days")}</li>
            <li>{t("Withdrawing of initial deposit will result in an equal deduction from the D2T deposit")}</li>
            <li>{t("Trading profits can be withdrawn immediately")}</li>
            <li>{t("Genuine trading activity only - no overrisking, no gambling, no manipulation.")}</li>
            <li>{t("For full details, please review the terms of TAG Markets")}</li>
          </ul>

          <h3>{t("Good to know:")}</h3>
          <p style={{ marginTop: "10px", marginBottom: "25px" }}>
            All trades need to be closed (no open/ongoing trades) before performing any operations (such as adding
            capital, withdrawal, upgrading) with your trading account. You are allowed to withdraw profits immediately,
            but doing so will decrease the part of portfolio increased by the tokens. A good strategy is to wait with
            first withdrawal until portfolio has grown by the amount of your deposit. For example:
          </p>
          <ul style={{ marginLeft: "40px" }}>
            <li>{t("Deposit 500 USD + 500 D2T, this gives you 12 000 portfolio")}</li>
            <li>{t("Grow the portfolio to 13 000, earning 1000 profit (8.3% net growth on your portfolio)")}</li>
            <li>{t("Withdraw all capital from trading account")}</li>
            <li>{t("Use part for a new trading account and take part out as profit")}</li>
            <li>{t("Use 500 USD from your balance + burn 500 D2T again to make a new 12 000 portfolio")}</li>
            <li>{t("Repeat the cycle")}</li>
          </ul>
        </div>
      </Modal>
    </>
  );
};
